import React, { useState, useEffect } from "react";
import * as Tabs from "@radix-ui/react-tabs";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAccessForm } from "../../hooks/useAccessForm";
import TabNavigation from "./accessComponents/TabNavigation";
import { useSupaContext } from "../../contexts/SupaContext";
import { BackgroundSection } from "./accessComponents/BackgroundSection";
import {
  LoginForm,
  RegistrationForm,
  RegistrationSuccess,
  ErrorMessage,
} from "./accessComponents/AccessModalComponents";
import {
  countries,
  documentTypes,
  validatePhone,
  validateDocument,
} from "../../scripts/generalScript";
import { ChevronLeft } from "lucide-react";

const AccessPage = ({ initialTab = "signin", defaultTab = "signin" }) => {
  const navigate = useNavigate();
  const { user } = useSupaContext();

  // Usar el hook useAccessForm compartido
  const {
    isRegistering,
    setIsRegistering,
    loading,
    error,
    showPassword,
    setShowPassword,
    signInData,
    setSignInData,
    handleSignIn,
    signUpData,
    setSignUpData,
    handleSignUp,
    passwordError,
    acceptTerms,
    setAcceptTerms,
  } = useAccessForm();

  // Estado adicional para la vista de registro exitoso
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab || defaultTab);

  // Estado para validaciones de contraseña y formulario
  const [validations, setValidations] = useState({
    hasUpperCase: false,
    hasNumber: false,
    minLength: false,
  });
  const [validationErrors, setValidationErrors] = useState({
    phone: "",
    document_value: "",
  });
  const [phonePrefix, setPhonePrefix] = useState("+57");
  const [formDisabled, setFormDisabled] = useState(false);
  const [fieldStatus, setFieldStatus] = useState({
    first_name: true,
    last_name: false,
    email: false,
    phone: false,
    nationality: false,
    document_type: false,
    document_value: false,
    password: false,
  });
  const [formProgress, setFormProgress] = useState(0);

  // Función para verificar si hay datos parciales en el formulario
  const hasPartialFormData = () => {
    // Si hay al menos un campo con datos pero no todos los campos requeridos están completos
    const hasAnyData = Object.values(signUpData).some(
      (value) => value !== null && value !== undefined && value !== ""
    );

    // Verificar si el formulario está parcialmente completo pero no finalizado
    return hasAnyData && formProgress < 100;
  };

  // Función para confirmar la salida con datos parciales
  const confirmExit = () => {
    if (isRegistering && hasPartialFormData()) {
      return window.confirm(
        "¿Estás seguro que deseas salir? Los datos ingresados no se guardarán."
      );
    }
    return true;
  };

  // Función para resetear completamente el formulario
  const resetFormData = () => {
    setSignUpData({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      nationality: "",
      document_type: "",
      document_value: "",
      password: "",
    });
    setAcceptTerms(false);
    setPhonePrefix("+57");
    setValidationErrors({
      phone: "",
      document_value: "",
    });
  };

  // Redirigir si ya está autenticado
  useEffect(() => {
    if (user) {
      navigate("/app");
    }
  }, [user, navigate]);

  // Efecto para actualizar el tab activo cuando cambia initialTab
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
      setIsRegistering(initialTab === "signup");
    }
  }, [initialTab, setIsRegistering]);

  // Validar la contraseña cuando cambie
  useEffect(() => {
    if (signUpData.password) {
      setValidations({
        hasUpperCase: /[A-Z]/.test(signUpData.password),
        hasNumber: /[0-9]/.test(signUpData.password),
        minLength: signUpData.password.length >= 8,
      });
    }
  }, [signUpData.password]);

  // Actualizar habilitación de campos y progreso del formulario
  useEffect(() => {
    // Actualizar habilitación de campos
    const newFieldStatus = {
      first_name: true,
      last_name: !!signUpData.first_name,
      email: !!signUpData.first_name && !!signUpData.last_name,
      phone:
        !!signUpData.first_name && !!signUpData.last_name && !!signUpData.email,
      nationality:
        !!signUpData.first_name &&
        !!signUpData.last_name &&
        !!signUpData.email &&
        !!signUpData.phone,
      document_type:
        !!signUpData.first_name &&
        !!signUpData.last_name &&
        !!signUpData.email &&
        !!signUpData.phone &&
        !!signUpData.nationality,
      document_value:
        !!signUpData.first_name &&
        !!signUpData.last_name &&
        !!signUpData.email &&
        !!signUpData.phone &&
        !!signUpData.nationality &&
        !!signUpData.document_type,
      password:
        !!signUpData.first_name &&
        !!signUpData.last_name &&
        !!signUpData.email &&
        !!signUpData.phone &&
        !!signUpData.nationality &&
        !!signUpData.document_type &&
        !!signUpData.document_value,
    };

    setFieldStatus(newFieldStatus);

    // Calcular progreso del formulario
    const totalFields = 9; // 8 campos + términos
    let completedFields = 0;

    if (signUpData.first_name) completedFields++;
    if (signUpData.last_name) completedFields++;
    if (signUpData.email) completedFields++;
    if (signUpData.phone) completedFields++;
    if (signUpData.nationality) completedFields++;
    if (signUpData.document_type) completedFields++;
    if (signUpData.document_value) completedFields++;
    if (signUpData.password) completedFields++;
    if (acceptTerms) completedFields++;

    setFormProgress((completedFields / totalFields) * 100);
  }, [signUpData, acceptTerms]);

  // Agregar evento beforeunload para confirmar salida si hay datos
  useEffect(() => {
    // Sólo agregar el listener si estamos en modo registro y hay datos parciales
    if (isRegistering && hasPartialFormData()) {
      const handleBeforeUnload = (e) => {
        // Mensaje estándar para confirmar salida (el texto exacto lo decide el navegador)
        e.preventDefault();
        e.returnValue = "";
        return "";
      };

      window.addEventListener("beforeunload", handleBeforeUnload);

      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  }, [isRegistering, signUpData, formProgress]);

  // Función modificada para manejar los cambios en los campos
  const handleChange = (field, value) => {
    // Validaciones específicas por campo
    if (field === "phone") {
      const validation = validatePhone(value);
      setValidationErrors((prev) => ({
        ...prev,
        phone: validation.errorMessage,
      }));
      value = validation.cleanValue;
    } else if (field === "document_value") {
      const validation = validateDocument(value, signUpData.document_type);
      setValidationErrors((prev) => ({
        ...prev,
        document_value: validation.errorMessage,
      }));
      value = validation.cleanValue;
    }

    // Actualizar datos según si es registro o login
    if (isRegistering) {
      setSignUpData((prev) => ({ ...prev, [field]: value }));
    } else {
      setSignInData((prev) => ({ ...prev, [field]: value }));
    }
  };

  // Función para detectar el proveedor de correo
  const getEmailProvider = (email) => {
    if (!email) return null;

    const domain = email.split("@")[1]?.toLowerCase();
    if (!domain) return null;

    if (domain.includes("gmail"))
      return { name: "Gmail", url: "https://mail.google.com" };
    if (domain.includes("outlook") || domain.includes("hotmail"))
      return { name: "Outlook", url: "https://outlook.live.com" };
    if (domain.includes("yahoo"))
      return { name: "Yahoo Mail", url: "https://mail.yahoo.com" };
    return null;
  };

  // Función de registro modificada para manejar el éxito
  const handleSignUpWithSuccess = async (e) => {
    e?.preventDefault();

    // Evitar múltiples envíos
    if (loading) return;

    setFormDisabled(true);

    // Formato de teléfono con prefijo
    const formattedPhone = signUpData.phone
      ? `${phonePrefix}${signUpData.phone}`
      : "";

    try {
      // Llamar a la función original con el teléfono formateado
      const success = await handleSignUp(e, formattedPhone);

      // Si el registro fue exitoso, mostrar la vista de éxito
      if (success === true) {
        setRegistrationSuccess(true);
        console.log(
          "Registro exitoso - AccessPage - Mostrar pantalla de éxito"
        );
      } else {
        setFormDisabled(false);
      }

      return success;
    } catch (error) {
      console.error("Error en registro:", error);
      setFormDisabled(false);
      return false;
    }
  };

  // Manejar el retorno a la pantalla de login desde la pantalla de éxito
  const handleReturnToLogin = () => {
    setIsRegistering(false);
    setRegistrationSuccess(false);
    setActiveTab("signin");
    resetFormData();
  };

  // Función para manejar el cambio de tab con confirmación
  const handleTabChange = (tab) => {
    if (tab === "signin" && isRegistering && hasPartialFormData()) {
      if (
        window.confirm(
          "¿Estás seguro que deseas salir? Los datos ingresados no se guardarán."
        )
      ) {
        setActiveTab(tab);
        setIsRegistering(false);
        setRegistrationSuccess(false);
        resetFormData();
      }
    } else {
      setActiveTab(tab);
      setIsRegistering(tab === "signup");
      // Si cambiamos a signin, resetear la vista de éxito
      if (tab === "signin") {
        setRegistrationSuccess(false);
      }
    }
  };

  if (user) {
    return null;
  }

  return (
    <div className="flex justify-center min-h-screen max-h-screen overflow-hidden">
      <div className="hidden lg:flex lg:w-1/2 relative  ">
        <BackgroundSection />
      </div>
      <div className="lg:w-1/2 w-full md:px-16 flex pt-16 justify-center overflow-auto">
        <div className="w-full flex min-h-[80vh] flex-col gap-4">
          <div className="flex w-full px-8 md:px-0">
            <motion.a
              href="/"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              // onClick={onBack}
              className="rounded-full w-8 h-8 border cursor-pointer flex items-center justify-center bg-gray-50 text-gray-800 hover:bg-gray-100 transition-colors flex-shrink-0"
            >
              <ChevronLeft className="w-4 h-4" />
            </motion.a>
          </div>
          <div className="text-center flex flex-col items-center ">
            <img
              src="/assets/logos/fluenti-logo-black-140.svg"
              alt="logo"
              className="h-20"
            />
            <p className="text-gray-600 text-lg p-0 m-0">
              Ingresa o regístrate para continuar
            </p>
            <div className="w-full flex justify-center items-center mt-4">
              <TabNavigation
                activeTab={activeTab}
                setActiveTab={handleTabChange}
              />
            </div>
          </div>

          <Tabs.Root value={activeTab} onValueChange={handleTabChange}>
            <AnimatePresence mode="wait">
              {activeTab === "signin" && !isRegistering && (
                <Tabs.Content key="signin-tab" value="signin">
                  <LoginForm
                    signInData={signInData}
                    handleChange={handleChange}
                    handleSignIn={handleSignIn}
                    loading={loading}
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                    setIsRegistering={() => {
                      setIsRegistering(true);
                      setActiveTab("signup");
                    }}
                  />
                </Tabs.Content>
              )}

              {activeTab === "signup" && isRegistering && (
                <Tabs.Content key="signup-tab" value="signup">
                  {registrationSuccess ? (
                    <RegistrationSuccess
                      key="registration-success"
                      email={signUpData.email}
                      setIsRegistering={handleReturnToLogin}
                      emailProvider={getEmailProvider(signUpData.email)}
                    />
                  ) : (
                    <div key="registration-form" className="relative">
                      <RegistrationForm
                        signUpData={signUpData}
                        handleChange={handleChange}
                        handleSignUp={handleSignUpWithSuccess}
                        fieldStatus={fieldStatus}
                        showPassword={showPassword}
                        setShowPassword={setShowPassword}
                        validations={validations}
                        passwordError={passwordError}
                        phonePrefix={phonePrefix}
                        setPhonePrefix={setPhonePrefix}
                        acceptTerms={acceptTerms}
                        setAcceptTerms={setAcceptTerms}
                        formProgress={formProgress}
                        loading={loading}
                        documentTypes={documentTypes}
                        countries={countries}
                        formDisabled={formDisabled}
                        validationErrors={validationErrors}
                      />
                    </div>
                  )}
                </Tabs.Content>
              )}
            </AnimatePresence>
          </Tabs.Root>

          {error && <ErrorMessage error={error} />}
        </div>
      </div>
    </div>
  );
};

export default AccessPage;
